import useConfig from '@/hooks/useConfig.ts';
import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import useSWRImmutableWithRollback from '@/hooks/useSWRWithRollback.ts';

import { DCTaskStatus, EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';
import { DCTaskTypeInternal } from '@/models/doc-cirrus/DCTaskTypes.ts';

import {
    getDoneTasksFromMultipleInstances,
    getOpenTasksWithTypesFromMultipleInstances,
} from '@/services/tasksService.ts';

import { useTaskViewStore } from '@/stores/taskViewStore.ts';

import { enrichTasks } from '@/utils/task-utils.ts';

interface TasksHook {
    open_tasks: EnrichedTask[];
    done_tasks: EnrichedTask[];
    task_types: DCTaskTypeInternal[];
    is_loading_open: boolean;
    is_loading_done: boolean;
    mutateOpenTasks: () => void;
    mutateDoneTasks: () => void;
    updateOrCreateTask: (operation: () => Promise<EnrichedTask>) => Promise<EnrichedTask>;
    removeTaskOptimistically: (task_id: string, operation: () => Promise<void>) => Promise<void>;
}

const useTasks = (): TasksHook => {
    const { is_completed_column_shown: include_done_tasks } = useTaskViewStore();
    const { configs, selected_location } = useConfig();
    const { instance_users_map } = usePvsPractitioners();

    const getOpenTasksWithTypesFromMultipleInstancesAndEnrich = async (): Promise<
        [EnrichedTask[], DCTaskTypeInternal[]]
    > => {
        const [tasks, task_types] = await getOpenTasksWithTypesFromMultipleInstances(configs);
        return [enrichTasks(tasks, task_types, instance_users_map), task_types];
    };

    const getDoneTasksFromMultipleInstancesAndEnrich = async (): Promise<EnrichedTask[]> => {
        const tasks = await getDoneTasksFromMultipleInstances(configs);
        return enrichTasks(tasks, task_types ?? [], instance_users_map);
    };

    const open_tasks_key = `open-tasks-${selected_location.key}`;
    const getOpenTasksKey = (): string | undefined => {
        if (instance_users_map && configs.length > 0) return open_tasks_key;
        return undefined;
    };

    const done_tasks_key = `done-tasks-${selected_location.key}`;
    const getDoneTasksKey = (): string | undefined => {
        if (instance_users_map && include_done_tasks && configs.length > 0 && task_types) return done_tasks_key;
        return undefined;
    };

    // Always fetch open tasks with task types
    const open_tasks_swr = useSWRImmutableWithRollback(
        getOpenTasksKey(),
        () => getOpenTasksWithTypesFromMultipleInstancesAndEnrich(),
        // eslint-disable-next-line no-restricted-syntax -- interval intentionally left dead: getTasksFromMultipleInstances drops failed instances and pages, so a live poll would delete tasks from the board (EPG-350)
        { refreshInterval: 30000 }
    );

    const [open_tasks, task_types] = open_tasks_swr.data ?? [];

    const done_tasks_swr = useSWRImmutableWithRollback(
        getDoneTasksKey(),
        () => getDoneTasksFromMultipleInstancesAndEnrich(),
        {
            // eslint-disable-next-line no-restricted-syntax -- interval intentionally left dead: getTasksFromMultipleInstances drops failed instances and pages, so a live poll would delete completed tasks from the board (EPG-350)
            refreshInterval: 30000,
        }
    );

    const done_data = done_tasks_swr.data ?? [];

    const updateOrCreateTask = async (operation: () => Promise<EnrichedTask>): Promise<EnrichedTask> => {
        // Perform the API operation
        const saved_task = await operation();

        const task_in_open: EnrichedTask | undefined = open_tasks?.find((task) => task.id === saved_task.id);

        const taskMatches = (task: EnrichedTask): boolean =>
            task.id === saved_task.id && task.instance_id === saved_task.instance_id;

        const shouldUpdateOpenTasks = Boolean(open_tasks && task_types && open_tasks_swr.data);
        const shouldUpdateDoneTasks = Boolean(include_done_tasks && done_data && done_tasks_swr.data);

        try {
            // Update open tasks optimistically
            if (shouldUpdateOpenTasks) {
                open_tasks_swr.optimisticUpdate((current) => {
                    if (!current) return current;
                    const [tasks, types] = current;
                    let new_tasks = [...tasks];

                    if (task_in_open) {
                        new_tasks = new_tasks.filter((task) => !taskMatches(task));
                    }

                    if (saved_task.status !== DCTaskStatus.DONE) {
                        new_tasks.push(saved_task);
                    }

                    return [new_tasks, types];
                });
            }

            // Update done tasks optimistically
            if (shouldUpdateDoneTasks) {
                done_tasks_swr.optimisticUpdate((current) => {
                    if (!current) return current;
                    let new_tasks = [...current];

                    if (!task_in_open) {
                        new_tasks = new_tasks.filter((task) => !taskMatches(task));
                    }

                    if (saved_task.status === DCTaskStatus.DONE) {
                        new_tasks.push(saved_task);
                    }

                    return new_tasks;
                });
            }

            return saved_task;
        } catch (error) {
            // On error, rollback optimistic updates
            open_tasks_swr.rollback();
            done_tasks_swr.rollback();
            throw error;
        }
    };

    const removeTaskOptimistically = async (task_id: string, operation: () => Promise<void>): Promise<void> => {
        const shouldUpdateOpenTasks = Boolean(open_tasks && open_tasks_swr.data);
        const shouldUpdateDoneTasks = Boolean(done_data && done_tasks_swr.data);

        try {
            if (shouldUpdateOpenTasks) {
                open_tasks_swr.optimisticUpdate((current) => {
                    if (!current) return current;
                    const [tasks, types] = current;
                    return [tasks.filter((task) => task.id !== task_id), types];
                });
            }

            if (shouldUpdateDoneTasks) {
                done_tasks_swr.optimisticUpdate((current) => {
                    if (!current) return current;
                    return current.filter((task) => task.id !== task_id);
                });
            }

            // Perform the API operation
            return await operation();
        } catch (error) {
            // On error, rollback optimistic updates
            open_tasks_swr.rollback();
            done_tasks_swr.rollback();
            throw error;
        }
    };

    // Extract tasks and task types from open_data
    return {
        open_tasks: open_tasks ?? [],
        task_types: task_types ?? [],
        done_tasks: done_data ?? [],
        is_loading_open: getOpenTasksKey() ? open_tasks_swr.isLoading : true,
        is_loading_done: include_done_tasks ? done_tasks_swr.isLoading : false,
        mutateOpenTasks: open_tasks_swr.mutate,
        mutateDoneTasks: done_tasks_swr.mutate,
        updateOrCreateTask: updateOrCreateTask,
        removeTaskOptimistically: removeTaskOptimistically,
    };
};

export default useTasks;
